import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { fmtDate, trafficLight } from "../lib/format";
import { Building2, User, Shield, Calendar, Check, Circle, Loader2, LogOut, FolderKanban } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientPortalDashboard() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!token) { setError("Token mancante"); return; }
    axios.get(`${API}/client-portal/session?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setSession(r.data);
        if (r.data.projects?.length) setSelectedId(r.data.projects[0].id);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Link non valido o scaduto"));
  }, [token]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
      <div className="text-center max-w-sm">
        <Shield className="h-8 w-8 mx-auto text-neutral-400 mb-3" />
        <p className="text-neutral-700 font-medium">{error}</p>
        <Link to="/portal" className="text-xs mt-3 underline text-neutral-500 inline-block">Richiedi un nuovo link</Link>
      </div>
    </div>
  );

  if (!session) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const primary = session.brand?.primary_color || "#0F3D3A";
  const selected = session.projects.find((p) => p.id === selectedId);

  return (
    <div className="min-h-screen bg-[#FAFAF7]" style={{ "--brand-primary": primary }}>
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-4">
          {session.brand?.logo_url ? <img src={session.brand.logo_url} className="h-9 w-9 object-contain" alt="logo" /> : <div className="h-9 w-9" style={{ background: primary }} />}
          <div className="flex-1">
            <div className="font-display text-lg font-medium">{session.brand?.studio_name || "amdirDesk"}</div>
            <div className="mono-label">Portale cliente · {session.client?.name}</div>
          </div>
          <div className="text-xs text-neutral-500 flex items-center gap-1"><Shield className="h-3 w-3" /> Dati economici nascosti</div>
          <Link to="/portal" data-testid="portal-logout" className="text-xs text-neutral-500 hover:text-neutral-800 flex items-center gap-1 ml-2">
            <LogOut className="h-3.5 w-3.5" /> Esci
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Project list */}
        <aside className="space-y-2">
          <div className="mono-label flex items-center gap-1.5 mb-2"><FolderKanban className="h-3 w-3" /> Le tue pratiche</div>
          {session.projects.length === 0 ? (
            <div className="border border-neutral-200 bg-white p-4 text-sm text-neutral-500">Nessuna pratica in corso.</div>
          ) : session.projects.map((p) => {
            const tl = trafficLight(p.deadline, p.delivered);
            const active = p.id === selectedId;
            return (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                data-testid={`portal-project-${p.id}`}
                className={`w-full text-left p-3 border ${active ? "bg-white border-neutral-800" : "bg-white border-neutral-200 hover:border-neutral-400"}`}
              >
                <div className="text-sm font-medium text-neutral-900 line-clamp-2">{p.title}</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className={`inline-block px-1.5 py-0.5 border font-mono text-[10px] font-semibold ${tl.className}`}>{tl.level === "green" ? "OK" : tl.label}</span>
                  <span className="mono-label">{fmtDate(p.deadline) || "—"}</span>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Detail */}
        {selected && (
          <div className="space-y-4">
            <div>
              <div className="mono-label flex items-center gap-1.5">
                {selected.type === "pa" ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
                {selected.type === "pa" ? "Pubblica Amministrazione" : "Cliente Privato"}
              </div>
              <h1 className="font-display text-3xl font-medium mt-1.5 leading-tight" data-testid="portal-project-title">{selected.title}</h1>
              {selected.description && <p className="text-sm text-neutral-600 mt-3">{selected.description}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="border border-neutral-200 bg-white p-5">
                <div className="mono-label">Stato</div>
                <div className={`inline-block mt-2 px-3 py-1.5 border font-mono text-xs font-semibold ${trafficLight(selected.deadline, selected.delivered).className}`}>
                  {selected.delivered ? "CONSEGNATA" : `SCADENZA · ${trafficLight(selected.deadline, selected.delivered).label}`}
                </div>
                {selected.won && !selected.delivered && <div className="mt-2 mono-label" style={{ color: primary }}>✓ Gara vinta / firmata</div>}
              </div>
              <div className="border border-neutral-200 bg-white p-5">
                <div className="mono-label">Scadenza</div>
                <div className="font-display text-xl font-medium mt-2 flex items-center gap-2"><Calendar className="h-4 w-4 text-neutral-500" />{fmtDate(selected.deadline)}</div>
              </div>
            </div>

            {(selected.cig || selected.cup || selected.stazione_appaltante) && (
              <div className="grid grid-cols-3 gap-0 border border-neutral-200 bg-white">
                <div className="p-4 border-r border-neutral-200"><div className="mono-label">CIG</div><div className="font-mono mt-1 text-sm">{selected.cig || "—"}</div></div>
                <div className="p-4 border-r border-neutral-200"><div className="mono-label">CUP</div><div className="font-mono mt-1 text-sm">{selected.cup || "—"}</div></div>
                <div className="p-4"><div className="mono-label">Tipo procedura</div><div className="text-sm mt-1">{selected.tipo_gara || "—"}</div></div>
              </div>
            )}

            <div className="border border-neutral-200 bg-white">
              <div className="p-5 border-b border-neutral-200">
                <div className="mono-label">SAL / Milestone</div>
                <h2 className="font-display text-xl font-medium mt-1">Avanzamento</h2>
              </div>
              <div className="divide-y divide-neutral-200">
                {selected.milestones.length === 0 && <div className="p-8 text-center text-sm text-neutral-500">Nessuna milestone definita</div>}
                {selected.milestones.map((m) => (
                  <div key={m.id} className="p-4 flex items-center gap-4">
                    {m.completed
                      ? <div className="h-5 w-5 flex items-center justify-center" style={{ background: primary }}><Check className="h-3 w-3 text-white" /></div>
                      : <Circle className="h-5 w-5 text-neutral-300" />}
                    <div className="flex-1">
                      <div className={`text-sm font-medium ${m.completed ? "text-neutral-500 line-through" : "text-neutral-900"}`}>{m.name}</div>
                      <div className="mono-label mt-0.5">{m.percentage}% del progetto {m.due_date && `· ${fmtDate(m.due_date)}`}{m.completed_at && ` · completata ${fmtDate(m.completed_at)}`}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
